import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { useConfigurationContext, useDropOffLocationContext } from '../../core/application/contexts'
import { getClickableLink } from '../../core/application/utils/linkUtils'
import { LazyDropOffSchedule, WasteType } from '../../core/domain/models'
import { EarthIcon, MapMarkerIcon, PhoneIcon } from '../icons'
import LeftArrowIcon from '../icons/LeftArrowIcon'
import AvailableWasteTypesModal from '../modal/availableWasteTypeModal/AvailableWasteTypesModal'
import ModalWithCloseCross from '../modal/ModalWithCloseCross'
import WasteTypeRow from '../shared/WasteTypeRow'
import DropOffScheduleDropdown from './DropOffScheduleDropdown'
import GpsButton from './GpsButton'
import RewardButton from './RewardButton'

export interface IDropOffLocationDetailsProps {
  id: string
  name: string
  address?: {
    line: string
    zipCode: string
    countryCode: string
    city: string
    province?: string
  }
  types: string[]
  coordinate: {
    latitude: number
    longitude: number
  }
  pictures: Array<string>
  onReturn: () => void
  phone?: string
  website?: string
  availableWasteTypes: Array<string>
  schedule?: LazyDropOffSchedule
  isOpenNow: boolean
}

const DropOffLocationDetails: React.FC<IDropOffLocationDetailsProps> = ({
                                                                          name,
                                                                          id,
                                                                          types,
                                                                          address,
                                                                          pictures,
                                                                          coordinate,
                                                                          availableWasteTypes,
                                                                          phone,
                                                                          website,
                                                                          schedule,
                                                                          onReturn,
                                                                          isOpenNow
                                                                        }: IDropOffLocationDetailsProps) => {
  const { wasteTypeList } = useConfigurationContext()
  const { setDisplayGetRewardDropoffId } = useDropOffLocationContext()

  const firstPicture = pictures.length > 0 ? pictures[0] : undefined
  const otherPictures = pictures ? pictures.slice(1) : []

  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const wastes: Array<WasteType> = wasteTypeList.filter((wasteType: WasteType) =>
    availableWasteTypes.includes(wasteType.id)
  )
  const onCloseModal = () => {
    setModalOpen(false)
  }

  const hasReward = types.includes('reward')

  let cityText = ''
  if (address?.zipCode) {
    cityText += address.zipCode
    cityText += ' '
  }
  if (address?.city) {
    cityText += address.city
  }

  return (
    <>
      <Container data-testid='dropOffLocationDetails-container'>
        <GoBackWrapper onClick={onReturn}>
          <LeftArrowIcon />
          <FormattedMessage id='DropOffDetails.GoBack.Label' />
        </GoBackWrapper>
        {firstPicture && <PictureWrapper image={firstPicture} />}
        <NameWrapper>{name}</NameWrapper>
        <ActionButtonList>
          {hasReward && <RewardButton onClick={() => setDisplayGetRewardDropoffId(id)} />}
          <GpsButton coordinate={coordinate} />
        </ActionButtonList>
        <InformationColumnWrapper hasImage={pictures.length > 0}>
          {address && (
            <InformationRow>
              <MapMarkerIcon />
              <div className='column'>
                <span>{address.line}</span>
                {address.province && <span>{address.province}</span>}
                <span>{cityText}</span>
              </div>
            </InformationRow>
          )}
          {schedule && <DropOffScheduleDropdown schedule={schedule} isOpenNow={isOpenNow} />}
          {wastes.length > 0 && (
            <WasteInformationRow>
              <WasteTypeRow wasteTypes={wastes} onShowAll={() => setModalOpen(true)} withShowAllButton={true} />
            </WasteInformationRow>
          )}
          {phone && (
            <InformationRow>
              <a href={`tel:${phone}`} target='_blank' rel='noreferrer'>
                <PhoneIcon />
                <span>{phone}</span>
              </a>
            </InformationRow>
          )}
          {website && (
            <InformationRow>
              <a href={getClickableLink(website)} target='_blank' rel='noreferrer'>
                <EarthIcon />
                <span>{website}</span>
              </a>
            </InformationRow>
          )}
          {otherPictures.length > 0 && (
            <PictureGridWrapper>
              <PictureLabelWrapper>
                <FormattedMessage id='DropOffDetails.AcceptedWastes.PicturesLabel' />
              </PictureLabelWrapper>
              <PictureGrid>
                {otherPictures.map((it, index) => (
                  <PictureGridItem data-testid='pictureGridItem-container' key={it} image={it} />
                ))}
              </PictureGrid>
            </PictureGridWrapper>
          )}
        </InformationColumnWrapper>
        <ModalWithCloseCross open={modalOpen} onClose={onCloseModal}>
          <AvailableWasteTypesModal name={name} wasteTypes={wastes} onClose={onCloseModal} />
        </ModalWithCloseCross>
      </Container>
    </>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const GoBackWrapper = styled.a`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;

  font-size: ${({ theme }) => theme.size.normal};
  color: ${({ theme }) => theme.color.black};

  cursor: pointer;

  margin-bottom: 24px;
`

interface IPictureWrapperProps {
  image: string
}

const PictureWrapper = styled.div<IPictureWrapperProps>`
  display: flex;
  flex-direction: column;
  height: 184px;
  width: 100%;
  background-image: ${({ image }) => `url(${image})`};
  background-size: cover;
  border-radius: 4px;
  margin-bottom: 12px;
`

const NameWrapper = styled.h1`
  font-size: ${({ theme }) => theme.size.big};
  font-family: ${({ theme }) => theme.font.semi};
  color: ${({ theme }) => theme.color.black};
  font-weight: ${({ theme }) => theme.weight.semi};

  margin-bottom: 12px;
`

const ActionButtonList = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  column-gap: 8px;
  margin-bottom: 24px;
`

interface InformationColumnWrapperProps {
  hasImage?: boolean
}

const InformationColumnWrapper = styled.div<InformationColumnWrapperProps>`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  max-height: ${({ hasImage }) => hasImage ? '30vh' : '60vh'};
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`

const InformationRow = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  justify-content: flex-start;
  align-items: center;
  font-size: ${({ theme }) => theme.size.normal};

  border-bottom: 1px solid ${({ theme }) => theme.color.lightGrey};

  padding: 16px 0;

  .column {
    display: flex;
    flex-direction: column;
  }

  a {
    cursor: pointer;
    color: ${({ theme }) => theme.color.black};
    text-decoration: none;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    column-gap: 5px;
  }
`

const WasteInformationRow = styled.div`
  display: flex;
  flex-direction: column;

  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.lightGrey};
`

const PictureGridWrapper = styled.div``

const PictureLabelWrapper = styled.div`
  font-size: ${({ theme }) => theme.size.normal};
  font-family: ${({ theme }) => theme.font.semi};
  color: ${({ theme }) => theme.color.black};
  font-weight: ${({ theme }) => theme.weight.bold};
  margin-top: 16px;
  margin-bottom: 8px;
`

const PictureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 8px;
  row-gap: 8px;
  margin-bottom: 24px;
`

interface IPictureGridItemProps {
  image: string
}

const PictureGridItem = styled.div<IPictureGridItemProps>`
  background-image: ${({ image }) => `url(${image})`};
  background-size: cover;
  border-radius: 4px;
  height: 94px;
  width: 100%;
`

export default DropOffLocationDetails
